'use strict'
import React, { useMemo, useState } from 'react'
import {
  Paper,
  Box,
  Typography,
  Collapse,
  IconButton
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts'
import { getCrewColor } from './crewColors'

const BASELINE_COLOR = '#7E57C2'
const SCENARIO_COLOR = '#66BB6A'
const SERVICE_COLOR = '#66BB6A'
const DRIVE_COLOR = '#FFA726'
const ESTIMATED_COLOR = '#42A5F5'
const ACTUAL_COLOR = '#EF5350'

function truncate (str, len = 14) {
  if (!str) return ''
  return str.length > len ? str.slice(0, len) + '…' : str
}

function ChartSection ({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Paper variant='outlined' style={{ marginBottom: 12 }}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={2}
        py={1}
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <Typography variant='subtitle2' style={{ fontWeight: 600 }}>{title}</Typography>
        <IconButton size='small'>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={open}>
        <Box px={2} pb={2}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default function FleetCharts ({ scenarioCrews = [], baselineCrews = [], crewIds = [], crewDisplayName }) {
  // Always prefer the Aspire route name over the legacy crew name for labels.
  const label = (c, len = 14) => truncate(
    crewDisplayName ? crewDisplayName(c.crewId, c.crewName) : c.crewName,
    len
  )

  // ─── Chart 1: Crew Load Bar Chart ───
  const crewLoadData = useMemo(() => {
    const map = new Map()
    baselineCrews.forEach(c => {
      map.set(c.crewId, { crewId: c.crewId, crewName: label(c), baselineTotal: c.weeklyTotalMinutes || 0, scenarioTotal: 0 })
    })
    scenarioCrews.forEach(c => {
      const existing = map.get(c.crewId) || { crewId: c.crewId, crewName: label(c), baselineTotal: 0, scenarioTotal: 0 }
      existing.scenarioTotal = c.weeklyTotalMinutes || 0
      if (!existing.crewName) existing.crewName = label(c)
      map.set(c.crewId, existing)
    })
    return [...map.values()].filter(d => d.crewId !== 'unassigned')
  }, [scenarioCrews, baselineCrews, crewDisplayName])

  // ─── Chart 2: Service vs Drive Stacked ───
  const serviceDriveData = useMemo(() => {
    return scenarioCrews
      .filter(c => c.crewId !== 'unassigned')
      .map(c => ({
        crewName: label(c),
        serviceMin: c.weeklyServiceMinutes || 0,
        driveMin: c.estimatedWeeklyDriveMinutes || 0
      }))
  }, [scenarioCrews, crewDisplayName])

  // ─── Chart 3: OT Comparison ───
  const otData = useMemo(() => {
    const map = new Map()
    baselineCrews.forEach(c => {
      map.set(c.crewId, { crewId: c.crewId, crewName: label(c), baselineOT: c.estimatedWeeklyOTMinutes || 0, scenarioOT: 0 })
    })
    scenarioCrews.forEach(c => {
      const existing = map.get(c.crewId) || { crewId: c.crewId, crewName: label(c), baselineOT: 0, scenarioOT: 0 }
      existing.scenarioOT = c.estimatedWeeklyOTMinutes || 0
      if (!existing.crewName) existing.crewName = label(c)
      map.set(c.crewId, existing)
    })
    return [...map.values()]
      .filter(d => d.crewId !== 'unassigned' && (d.baselineOT > 0 || d.scenarioOT > 0))
  }, [scenarioCrews, baselineCrews, crewDisplayName])

  // ─── Chart 4: Balance/Efficiency Radar ───
  const radarData = useMemo(() => {
    const topCrews = [...scenarioCrews]
      .filter(c => c.crewId !== 'unassigned')
      .sort((a, b) => (b.weeklyTotalMinutes || 0) - (a.weeklyTotalMinutes || 0))
      .slice(0, 5)

    if (!topCrews.length) return { axes: [], crews: [] }

    const maxOT = Math.max(1, ...topCrews.map(c => c.estimatedWeeklyOTMinutes || 0))
    const maxProps = Math.max(1, ...topCrews.map(c => c.propertyCount || 0))

    const axes = ['OT Minutes', 'Drive %', 'Balance', 'Properties']
    const crews = topCrews.map(c => {
      const total = c.weeklyTotalMinutes || 1
      const driveMin = c.estimatedWeeklyDriveMinutes || 0
      return {
        crewId: c.crewId,
        crewName: label(c, 12),
        values: {
          'OT Minutes': Math.min(100, ((c.estimatedWeeklyOTMinutes || 0) / maxOT) * 100),
          'Drive %': (driveMin / total) * 100,
          Balance: Math.min(100, Math.max(0, (c.balanceScore || 0) * 50)),
          Properties: ((c.propertyCount || 0) / maxProps) * 100
        }
      }
    })

    // Transform for Recharts RadarChart: one object per axis, one key per crew
    const data = axes.map(axis => {
      const point = { axis }
      crews.forEach(c => { point[c.crewName] = Math.round(c.values[axis]) })
      return point
    })

    return { data, crews }
  }, [scenarioCrews, crewDisplayName])

  // ─── Chart 5: Estimated vs Actual Service Time ───
  const estVsActualData = useMemo(() => {
    return scenarioCrews
      .filter(c => c.crewId !== 'unassigned')
      .map(c => ({
        crewName: label(c),
        estimatedSvcMin: c.weeklyServiceMinutes || 0,
        actualSvcMin: c.actualWeeklyServiceMinutes || 0
      }))
  }, [scenarioCrews, crewDisplayName])

  if (!scenarioCrews.length && !baselineCrews.length) return null

  return (
    <Box>
      {/* Chart 1: Crew Load */}
      <ChartSection title='Crew Load Comparison (Weekly Minutes)' defaultOpen>
        {crewLoadData.length > 0 ? (
          <ResponsiveContainer width='100%' height={Math.max(280, crewLoadData.length * 36)}>
            <BarChart data={crewLoadData} layout='vertical' margin={{ left: 20, right: 20 }}>
              <XAxis type='number' />
              <YAxis type='category' dataKey='crewName' width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey='baselineTotal' name='Baseline' fill={BASELINE_COLOR} barSize={14} />
              <Bar dataKey='scenarioTotal' name='Scenario' fill={SCENARIO_COLOR} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant='body2' color='textSecondary'>No crew data available</Typography>
        )}
      </ChartSection>

      {/* Chart 2: Service vs Drive */}
      <ChartSection title='Service vs Drive Time (Scenario)'>
        {serviceDriveData.length > 0 ? (
          <ResponsiveContainer width='100%' height={Math.max(280, serviceDriveData.length * 36)}>
            <BarChart data={serviceDriveData} layout='vertical' margin={{ left: 20, right: 20 }}>
              <XAxis type='number' />
              <YAxis type='category' dataKey='crewName' width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey='serviceMin' name='Service' stackId='a' fill={SERVICE_COLOR} />
              <Bar dataKey='driveMin' name='Drive' stackId='a' fill={DRIVE_COLOR} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant='body2' color='textSecondary'>No crew data available</Typography>
        )}
      </ChartSection>

      {/* Chart 3: OT Comparison */}
      <ChartSection title='Overtime Comparison'>
        {otData.length > 0 ? (
          <ResponsiveContainer width='100%' height={Math.max(250, otData.length * 40)}>
            <BarChart data={otData} layout='vertical' margin={{ left: 20, right: 20 }}>
              <XAxis type='number' />
              <YAxis type='category' dataKey='crewName' width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey='baselineOT' name='Baseline OT' fill={BASELINE_COLOR} barSize={14} />
              <Bar dataKey='scenarioOT' name='Scenario OT' fill={SCENARIO_COLOR} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant='body2' color='textSecondary'>No crews with overtime</Typography>
        )}
      </ChartSection>

      {/* Chart 4: Balance/Efficiency Radar */}
      <ChartSection title='Balance & Efficiency Radar (Top 5 Crews)'>
        {radarData.crews && radarData.crews.length > 0 ? (
          <ResponsiveContainer width='100%' height={380}>
            <RadarChart data={radarData.data}>
              <PolarGrid />
              <PolarAngleAxis dataKey='axis' tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              {radarData.crews.map((crew) => (
                <Radar
                  key={crew.crewId}
                  name={crew.crewName}
                  dataKey={crew.crewName}
                  stroke={getCrewColor(crew.crewId, crewIds)}
                  fill={getCrewColor(crew.crewId, crewIds)}
                  fillOpacity={0.15}
                />
              ))}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant='body2' color='textSecondary'>No crew data for radar chart</Typography>
        )}
      </ChartSection>

      {/* Chart 5: Estimated vs Actual Service Time */}
      <ChartSection title='Estimated vs Actual Service Time'>
        {estVsActualData.length > 0 ? (
          <ResponsiveContainer width='100%' height={Math.max(280, estVsActualData.length * 36)}>
            <BarChart data={estVsActualData} layout='vertical' margin={{ left: 20, right: 20 }}>
              <XAxis type='number' />
              <YAxis type='category' dataKey='crewName' width={120} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey='estimatedSvcMin' name='Estimated' fill={ESTIMATED_COLOR} barSize={14} />
              <Bar dataKey='actualSvcMin' name='Actual (Median)' fill={ACTUAL_COLOR} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant='body2' color='textSecondary'>No crew data available</Typography>
        )}
      </ChartSection>
    </Box>
  )
}
